import React, { useState, useMemo } from 'react';
import { COMPREHENSIVE_DENTAL_INCIDENTS } from '../lib/constants';

/**
 * Filter Tabs Definition
 */
export const INCIDENT_TABS = [
  { id: 'frequent', label: '⚡ ที่พบบ่อย', icon: '⚡' },
  { id: 'all', label: '📋 รวมทุกหมวด', icon: '📋' },
  { id: 'severity', label: '🎯 เรียงตามความรุนแรง', icon: '🎯' },
  { id: 'clinical', label: '🩺 คลินิก & หัตถการ', icon: '🩺' },
  { id: 'cssd', label: '🧼 จ่ายกลาง & ฆ่าเชื้อ', icon: '🧼' },
  { id: 'emergency_pharma', label: '💉 ยา & ภาวะฉุกเฉิน', icon: '💉' },
  { id: 'staff_safety', label: '🦺 ความปลอดภัยบุคลากร', icon: '🦺' },
  { id: 'equipment_system', label: '⚙️ อุปกรณ์ & ระบบ', icon: '⚙️' }
];

/**
 * 4 Severity Tiers (Based on 2P Safety / Level A-I & Level 1-5)
 */
export const SEVERITY_TIERS = [
  {
    id: 'severe',
    title: '🔴 ฉุกเฉิน / วิกฤต (Severe: Level G-I / Level 4-5)',
    shortTitle: '🔴 ฉุกเฉิน/วิกฤต',
    subtitle: 'เสี่ยงอันตรายถาวร / ภาวะคุกคามชีวิต ต้องรายงานและตอบสนองทันที',
    color: '#dc2626',
    bgColor: '#fef2f2',
    borderColor: '#fecaca',
    match: (inc) => ['G', 'H', 'I', '4', '5'].includes(String(inc.severity_level).toUpperCase()) || (inc.severity_score >= 4)
  },
  {
    id: 'harm',
    title: '🟠 อันตราย / ต้องรักษา (Harm: Level E-F / Level 3)',
    shortTitle: '🟠 อันตราย/ต้องรักษา',
    subtitle: 'เกิดอันตรายชั่วคราว ต้องให้การบำบัดรักษาหรือยืดระยะเวลารักษา',
    color: '#ea580c',
    bgColor: '#fff7ed',
    borderColor: '#fed7aa',
    match: (inc) => ['E', 'F', '3'].includes(String(inc.severity_level).toUpperCase()) || (inc.severity_score === 3)
  },
  {
    id: 'noharm',
    title: '🟡 เฝ้าระวัง / ไม่เกิดอันตราย (No Harm: Level C-D / Level 2)',
    shortTitle: '🟡 เฝ้าระวัง/ไม่เกิดอันตราย',
    subtitle: 'เกิดความคลาดเคลื่อนถึงผู้ป่วยหรือเครื่องมือชำรุด แต่ไม่เกิดอันตราย',
    color: '#d97706',
    bgColor: '#fffbeb',
    borderColor: '#fde68a',
    match: (inc) => ['C', 'D', '2'].includes(String(inc.severity_level).toUpperCase()) || (inc.severity_score === 2)
  },
  {
    id: 'nearmiss',
    title: '🔵 เกือบพลาด (Near Miss: Level A-B / Level 1)',
    shortTitle: '🔵 เกือบพลาด (Near Miss)',
    subtitle: 'ตรวจพบและยับยั้งได้ก่อนถึงผู้ป่วย / สภาพแวดล้อมที่มีโอกาสเกิดอันตราย',
    color: '#0284c7',
    bgColor: '#f0f9ff',
    borderColor: '#bae6fd',
    match: (inc) => ['A', 'B', '1'].includes(String(inc.severity_level).toUpperCase()) || (inc.severity_score === 1)
  }
];

/**
 * Helper to compute severity badge visual style
 */
export const getSeverityBadgeStyle = (level, score = 1) => {
  const lvl = String(level || 'C').toUpperCase();
  if (['G', 'H', 'I', '4', '5'].includes(lvl) || score >= 4) {
    return { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5', label: lvl };
  }
  if (['E', 'F', '3'].includes(lvl) || score === 3) {
    return { bg: '#ffedd5', text: '#c2410c', border: '#fdba74', label: lvl };
  }
  if (['C', 'D', '2'].includes(lvl) || score === 2) {
    return { bg: '#fef3c7', text: '#b45309', border: '#fcd34d', label: lvl };
  }
  return { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc', label: lvl };
};

/**
 * Domain icon helper
 */
const getDomainIcon = (group, domain) => {
  if (group === 'emergency_pharma') return '💉';
  if (group === 'staff_safety') return '🦺';
  if (group === 'equipment_system') return '⚙️';
  if (domain === 'cssd' || group === 'cssd') return '🧼';
  return '🩺';
};

/**
 * DentalIncidentQuickSelector
 * คอมโพเนนต์เลือกเหตุการณ์พบบ่อยแบบครบวงจร (Dental Risk Comprehensive Quick Selector)
 * - 70+ รายการอุบัติการณ์ครอบคลุมทั้งคลินิก, งานจ่ายกลาง CSSD, ยา/ฉุกเฉิน, บุคลากร และระบบ
 * - ค้นหาแบบ Real-time ตามคำสำคัญ (เช่น เข็ม, ยาชา, หัก, เปียก, ไฟดับ, ตกค้าง, สำลัก, ฉีก)
 * - แท็บจัดหมวดหมู่: "ที่พบบ่อย", "รวมทุกหมวด", "เรียงตามความรุนแรง", หมวดเฉพาะทาง
 * - แตะ 1 ครั้ง Auto-fill ครบทุกฟิลด์: Title, Severity, Domain, Category, Subcategory, Location
 */
const DentalIncidentQuickSelector = ({
  selectedTitle = '',
  onSelectIncident = () => {},
  initialTab = 'frequent',
  maxHeight = '180px'
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const cleanSearch = searchTerm.trim().toLowerCase();

  // Filtered Items based on active tab and search
  const displayedItems = useMemo(() => {
    const matchesSearch = (item) => {
      if (!cleanSearch) return true;
      if (item.tag.toLowerCase().includes(cleanSearch)) return true;
      if (item.category && item.category.toLowerCase().includes(cleanSearch)) return true;
      if (item.sub_category && item.sub_category.toLowerCase().includes(cleanSearch)) return true;
      if (item.keywords && item.keywords.some((kw) => kw.toLowerCase().includes(cleanSearch))) return true;
      return false;
    };

    // If search term is present and tab is not 'all' or 'severity', we search across active tab first
    if (cleanSearch) {
      if (activeTab === 'all' || activeTab === 'severity') {
        return COMPREHENSIVE_DENTAL_INCIDENTS.filter(matchesSearch);
      }
      if (activeTab === 'frequent') {
        const freqMatches = COMPREHENSIVE_DENTAL_INCIDENTS.filter((item) => item.is_frequent && matchesSearch(item));
        // If no matches in frequent, automatically search all to help user find what they need
        return freqMatches.length > 0
          ? freqMatches
          : COMPREHENSIVE_DENTAL_INCIDENTS.filter(matchesSearch);
      }
      // Specific category tab
      const tabMatches = COMPREHENSIVE_DENTAL_INCIDENTS.filter(
        (item) => item.group === activeTab && matchesSearch(item)
      );
      // Fallback to all if zero found in current tab
      return tabMatches.length > 0
        ? tabMatches
        : COMPREHENSIVE_DENTAL_INCIDENTS.filter(matchesSearch);
    }

    // No search term - standard tab filtering
    if (activeTab === 'all' || activeTab === 'severity') {
      return COMPREHENSIVE_DENTAL_INCIDENTS;
    }
    if (activeTab === 'frequent') {
      return COMPREHENSIVE_DENTAL_INCIDENTS.filter((item) => item.is_frequent);
    }
    return COMPREHENSIVE_DENTAL_INCIDENTS.filter((item) => item.group === activeTab);
  }, [activeTab, cleanSearch]);

  // Group items by severity tiers when activeTab is 'severity' (or search in severity tab)
  const severityGrouped = useMemo(() => {
    if (activeTab !== 'severity') return null;
    return SEVERITY_TIERS.map((tier) => ({
      tier,
      items: displayedItems.filter((item) => tier.match(item))
    }));
  }, [activeTab, displayedItems]);

  // Handle clicking on an incident chip
  const handleChipClick = (item) => {
    onSelectIncident(item);
  };

  // Find currently selected incident metadata if any
  const currentSelectedMeta = useMemo(() => {
    if (!selectedTitle) return null;
    return COMPREHENSIVE_DENTAL_INCIDENTS.find(
      (item) => item.tag === selectedTitle || selectedTitle.includes(item.tag)
    );
  }, [selectedTitle]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        backgroundColor: '#f8fafc',
        border: '1.5px solid #e2e8f0',
        borderRadius: '14px',
        padding: '12px 14px'
      }}
    >
      {/* ==================== HEADER & SEARCH BAR ==================== */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '1.15em' }}>⚡</span>
            <label
              htmlFor="incident-quick-search-input"
              style={{ fontSize: '0.85em', fontWeight: '800', color: '#1e293b' }}
            >
              เลือกเหตุการณ์พบบ่อย (แตะ 1 ครั้งเพื่อกรอกหัวข้อและประเมินทันที)
            </label>
            <span
              style={{
                fontSize: '0.7em',
                fontWeight: '700',
                padding: '2px 7px',
                borderRadius: '9999px',
                backgroundColor: '#e0f2fe',
                color: '#0369a1'
              }}
            >
              {displayedItems.length} รายการ
            </span>
          </div>

          {/* Expand / Collapse toggle */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'transparent',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '2px 8px',
              fontSize: '0.75em',
              fontWeight: '600',
              color: '#475569',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={isExpanded ? 'ย่อมุมมอง' : 'ขยายมุมมองเพื่อดูรายการทั้งหมด'}
          >
            <span>{isExpanded ? '▲ ย่อมุมมอง' : '▼ ขยายมุมมอง'}</span>
          </button>
        </div>

        {/* Real-time Instant Search Input */}
        <div style={{ position: 'relative', width: '100%' }}>
          <span
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.9em',
              color: '#94a3b8',
              pointerEvents: 'none'
            }}
          >
            🔍
          </span>
          <input
            id="incident-quick-search-input"
            type="text"
            placeholder="พิมพ์คำค้นหาด่วน เช่น เข็ม, ยาชา, หัก, เปียก, ไฟดับ, ตกค้าง, สำลัก, ฉีก, สปอร์..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 32px 7px 32px',
              borderRadius: '9px',
              border: cleanSearch ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              fontSize: '0.84em',
              boxSizing: 'border-box',
              outline: 'none',
              boxShadow: cleanSearch ? '0 0 0 3px rgba(2, 132, 199, 0.15)' : 'none',
              transition: 'all 0.15s ease'
            }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#e2e8f0',
                border: 'none',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                color: '#475569',
                cursor: 'pointer',
                padding: 0
              }}
              title="ล้างคำค้นหา"
            >
              ✕
            </button>
          )}
        </div>

        {/* Selected Chip Indicator (if currently selected) */}
        {selectedTitle && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '4px 10px',
              fontSize: '0.78em'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
              <span style={{ color: '#2563eb', fontWeight: '800' }}>✓ เลือกแล้ว:</span>
              <span
                style={{
                  fontWeight: '700',
                  color: '#1e3a8a',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '380px'
                }}
              >
                {selectedTitle}
              </span>
              {currentSelectedMeta && (
                <span
                  style={{
                    fontSize: '0.85em',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: '700',
                    backgroundColor: getSeverityBadgeStyle(currentSelectedMeta.severity_level, currentSelectedMeta.severity_score).bg,
                    color: getSeverityBadgeStyle(currentSelectedMeta.severity_level, currentSelectedMeta.severity_score).text
                  }}
                >
                  {currentSelectedMeta.domain === 'non_clinical' ? `Lv ${currentSelectedMeta.severity_level}` : `Level ${currentSelectedMeta.severity_level}`}
                </span>
              )}
            </div>
            <span style={{ fontSize: '0.8em', color: '#64748b' }}>แตะรายการอื่นเพื่อเปลี่ยน</span>
          </div>
        )}

        {/* ==================== CATEGORY / VIEW FILTER TABS ==================== */}
        <div
          style={{
            display: 'flex',
            gap: '5px',
            overflowX: 'auto',
            paddingBottom: '3px',
            scrollbarWidth: 'thin'
          }}
        >
          {INCIDENT_TABS.map((tab) => {
            const isTabActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '20px',
                  border: isTabActive ? '1.5px solid #0284c7' : '1px solid #e2e8f0',
                  backgroundColor: isTabActive ? '#0284c7' : '#ffffff',
                  color: isTabActive ? '#ffffff' : '#475569',
                  fontSize: '0.76em',
                  fontWeight: isTabActive ? '700' : '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  boxShadow: isTabActive ? '0 2px 4px rgba(2, 132, 199, 0.25)' : 'none'
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ==================== INCIDENT CHIPS CONTAINER ==================== */}
      <div
        style={{
          maxHeight: isExpanded ? '380px' : maxHeight,
          overflowY: 'auto',
          padding: '4px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        {/* VIEW 1: SORTED BY SEVERITY TIERS */}
        {activeTab === 'severity' && severityGrouped ? (
          severityGrouped.map(({ tier, items }) => {
            if (items.length === 0) return null;
            return (
              <div
                key={tier.id}
                style={{
                  backgroundColor: tier.bgColor,
                  border: `1.5px solid ${tier.borderColor}`,
                  borderRadius: '10px',
                  padding: '8px 10px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.8em', fontWeight: '800', color: tier.color }}>
                    {tier.title}
                  </span>
                  <span style={{ fontSize: '0.7em', color: tier.color, fontWeight: '700' }}>
                    {items.length} รายการ
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {items.map((item) => {
                    const isSelected = selectedTitle === item.tag;
                    const badgeStyle = getSeverityBadgeStyle(item.severity_level, item.severity_score);
                    return (
                      <button
                        key={item.id || item.tag}
                        type="button"
                        onClick={() => handleChipClick(item)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          borderRadius: '9999px',
                          backgroundColor: isSelected ? '#0284c7' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#1e293b',
                          border: isSelected ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                          fontSize: '0.78em',
                          fontWeight: isSelected ? '700' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 2px 6px rgba(2, 132, 199, 0.3)' : '0 1px 2px rgba(0,0,0,0.04)'
                        }}
                      >
                        <span>{getDomainIcon(item.group, item.domain)}</span>
                        <span>{isSelected ? `✓ ${item.tag}` : `+ ${item.tag}`}</span>
                        <span
                          style={{
                            fontSize: '0.82em',
                            fontWeight: '800',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : badgeStyle.bg,
                            color: isSelected ? '#ffffff' : badgeStyle.text,
                            border: isSelected ? 'none' : `1px solid ${badgeStyle.border}`
                          }}
                        >
                          {item.severity_level}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          /* VIEW 2: STANDARD CHIPS (TAG / CATEGORY FILTER) */
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {displayedItems.length > 0 ? (
              displayedItems.map((item) => {
                const isSelected = selectedTitle === item.tag;
                const badgeStyle = getSeverityBadgeStyle(item.severity_level, item.severity_score);
                return (
                  <button
                    key={item.id || item.tag}
                    type="button"
                    onClick={() => handleChipClick(item)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 11px',
                      borderRadius: '9999px',
                      backgroundColor: isSelected ? '#0284c7' : '#ffffff',
                      color: isSelected ? '#ffffff' : '#334155',
                      border: isSelected ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                      fontSize: '0.78em',
                      fontWeight: isSelected ? '700' : '600',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: isSelected ? '0 2px 6px rgba(2, 132, 199, 0.3)' : '0 1px 2px rgba(0,0,0,0.03)'
                    }}
                  >
                    <span>{getDomainIcon(item.group, item.domain)}</span>
                    <span>{isSelected ? `✓ ${item.tag}` : `+ ${item.tag}`}</span>
                    <span
                      style={{
                        fontSize: '0.82em',
                        fontWeight: '800',
                        padding: '1px 5px',
                        borderRadius: '4px',
                        backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : badgeStyle.bg,
                        color: isSelected ? '#ffffff' : badgeStyle.text,
                        border: isSelected ? 'none' : `1px solid ${badgeStyle.border}`
                      }}
                    >
                      {item.severity_level}
                    </span>
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  width: '100%',
                  padding: '16px',
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  border: '1px dashed #cbd5e1',
                  color: '#64748b',
                  fontSize: '0.82em'
                }}
              >
                <span>🔍 ไม่พบเหตุการณ์ที่ตรงกับคำค้นหา "{searchTerm}"</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#94a3b8' }}>
                  ท่านสามารถพิมพ์สรุปเหตุการณ์เองในช่องด้านล่างได้ทันที หรือกดปุ่ม ✕ เพื่อล้างคำค้นหา
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DentalIncidentQuickSelector;
